<div class="min-h-screen bg-gray-50 dark:bg-slate-900 p-6 space-y-8">
  <!-- HEADER -->
  <header class="flex items-center justify-between">
    <div>
      <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">
        🏪 Tableau de bord – {{ businessName || "Mon commerce" }}
      </h1>
      <div class="flex flex-wrap items-center gap-2 mt-2">
        <!-- Badge Type d'activité -->
        @if (activityType && activityTypeLabels[activityType]) {
          <span
            class="inline-block px-3 py-1 text-xs font-semibold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 rounded-full"
          >
            {{ activityTypeLabels[activityType] }}
          </span>
        }

        <!-- Badge Services -->
        @if (userServices.length > 0) {
          <span
            class="inline-block px-3 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 rounded-full"
          >
            Service :
            @for (service of userServices; track service; let last = $last) {
              <span
                >{{ serviceLabels[service] }}
                @if (!last) {
                  <span>, </span>
                }
              </span>
            }
          </span>
        }

        <!-- Badge Affluence -->
        @if (affluence && affluenceLabels[affluence]) {
          <span
            [ngClass]="{
              'text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30':
                affluence === 'low',
              'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30':
                affluence === 'medium',
              'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30':
                affluence === 'high',
            }"
            class="inline-block px-3 py-1 text-xs font-semibold rounded-full"
          >
            {{ affluenceLabels[affluence].icon }} Affluence :
            {{ affluenceLabels[affluence].label }}
          </span>
        }
      </div>
      <!-- Adresse -->
      @if (userAddress) {
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          📍 {{ userAddress }}
        </p>
      }
    </div>
    <!-- Menu card button -->
    <button
      (click)="showMenuCard = true"
      class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
      title="Voir ma carte / menu"
    >
      📋 Carte du menu
    </button>
  </header>

  <!-- Menu card modal -->
  @if (showMenuCard) {
    <app-menu-card
      merchantType="fixed"
      [businessName]="businessName"
      (closed)="showMenuCard = false"
    />
  }

  <!-- KPIs -->
  <section class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
    @for (stat of stats; track stat) {
      <div
        class="rounded-xl shadow-sm dark:shadow-slate-900/30 p-6 hover:shadow-md dark:shadow-slate-900/50 transition-shadow"
        [ngClass]="
          stat.label === 'Économies réalisées'
            ? 'bg-yellow-100 dark:bg-yellow-900/30'
            : 'bg-white dark:bg-slate-800'
        "
      >
        <div class="flex items-start justify-between mb-3">
          <span class="text-3xl">{{ stat.icon }}</span>
          @if (stat.trend) {
            <span
              class="text-xs px-2 py-1 rounded-full"
              [ngClass]="{
                'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300':
                  stat.trendUp,
                'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300':
                  !stat.trendUp,
              }"
            >
              {{ stat.trendUp ? "↗" : "↘" }} {{ stat.trend }}
            </span>
          }
        </div>
        <p class="text-sm text-gray-600 dark:text-gray-400">{{ stat.label }}</p>
        <p
          class="text-3xl font-bold mt-2"
          [ngClass]="
            stat.label === 'Économies réalisées'
              ? 'text-yellow-800 dark:text-yellow-300'
              : 'text-purple-700 dark:text-purple-300'
          "
        >
          {{ stat.value }}
        </p>
      </div>
    }
  </section>

  <!-- Calendrier multi-période -->
  <section
    class="rounded-2xl bg-white dark:bg-slate-900 shadow-lg dark:shadow-inner border border-gray-200 dark:border-slate-800"
  >
    <!-- ─── Header ─── -->
    <div class="px-6 pt-5 pb-0">
      <div
        class="flex flex-col sm:flex-row sm:items-start justify-between gap-3"
      >
        <!-- Titre + plage de dates -->
        <div>
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
            📆 Calendrier
          </h2>
          <p
            class="text-sm text-gray-500 dark:text-slate-400 mt-0.5 capitalize"
          >
            {{ getCalendarRangeLabel() }}
          </p>
        </div>
      </div>

      <!-- Tabs de vue + navigation -->
      <div
        class="flex items-center justify-between mt-4 border-b border-gray-200 dark:border-slate-700"
      >
        <div class="flex items-center gap-0">
          @for (view of calendarViews; track view) {
            <button
              (click)="setCalendarView(view)"
              class="px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px"
              [class]="
                calendarView() === view
                  ? 'border-purple-600 text-purple-700 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
              "
            >
              {{ CALENDAR_VIEW_META[view].label }}
            </button>
          }
        </div>
        <div class="flex items-center gap-2 mb-1">
          <div
            class="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1"
          >
            <button
              (click)="navigateCalendar(-1)"
              class="w-7 h-7 flex items-center justify-center rounded-md text-gray-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-all"
            >
              ‹
            </button>
            <button
              (click)="resetCalendarToToday()"
              class="px-2.5 h-7 text-xs font-medium rounded-md text-gray-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-all"
              [class.bg-white]="calendarOffset() === 0"
              [class.dark:bg-slate-700]="calendarOffset() === 0"
              [class.shadow-sm]="calendarOffset() === 0"
            >
              Aujourd'hui
            </button>
            <button
              (click)="navigateCalendar(1)"
              class="w-7 h-7 flex items-center justify-center rounded-md text-gray-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-all"
            >
              ›
            </button>
          </div>
          <button
            (click)="openPeriodForm(null)"
            class="px-3 py-1.5 rounded-lg text-sm font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 border border-indigo-200 dark:border-indigo-700 transition-all"
          >
            + Période
          </button>
        </div>
      </div>
    </div>

    <!-- ─── Légende des périodes actives ─── -->
    @if (merchantPeriods().length > 0) {
      <div class="flex flex-wrap gap-2 px-6 pt-3">
        @for (period of merchantPeriods(); track period._id) {
          <div
            class="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
            [style.border-color]="period.color || '#a78bfa'"
            [style.background-color]="(period.color || '#a78bfa') + '22'"
            [style.color]="period.color || '#7c3aed'"
          >
            <span>{{ period.label }}</span>
            <button
              (click)="openPeriodForm(period)"
              class="opacity-60 hover:opacity-100"
              title="Modifier"
            >
              ✏️
            </button>
            <button
              (click)="deletePeriod(period)"
              class="opacity-60 hover:opacity-100"
              title="Supprimer"
            >
              ✕
            </button>
          </div>
        }
      </div>
    }

    <!-- ─── Vue 7 jours ─── -->
    @if (calendarView() === "1w") {
      <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 p-6">
        @for (day of calendarDays; track day.date) {
          <div
            (click)="selectDay(day)"
            class="relative cursor-pointer rounded-xl border transition-all duration-200 hover:shadow-md overflow-hidden"
            [class]="
              selectedDay && isSameDay(day.date, selectedDay.date)
                ? 'border-purple-400 ring-2 ring-purple-500 shadow-lg'
                : day.isToday
                  ? 'border-blue-300 dark:border-blue-600'
                  : day.specialPeriods?.length
                    ? 'border-gray-200 dark:border-slate-700 hover:opacity-90'
                    : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-750'
            "
            [style.background-color]="
              day.specialPeriods?.length
                ? (day.specialPeriods![0].color || '#a78bfa') + '22'
                : null
            "
          >
            <div class="p-3">
              <!-- Jour + date -->
              <div class="flex items-start justify-between mb-3">
                <div>
                  <div
                    class="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500"
                  >
                    {{ day.date | date: "EEE" }}
                  </div>
                  @if (day.isToday) {
                    <div
                      class="mt-0.5 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold"
                    >
                      {{ day.date | date: "d" }}
                    </div>
                  } @else {
                    <div
                      class="mt-0.5 text-2xl font-bold text-gray-900 dark:text-white leading-none"
                    >
                      {{ day.date | date: "d" }}
                    </div>
                  }
                </div>
                <!-- Météo -->
                @if (day.weather) {
                  <div class="text-right">
                    <div class="text-lg leading-none">
                      {{ day.weather.icon }}
                    </div>
                    <div
                      class="text-xs font-semibold text-gray-600 dark:text-slate-300 mt-0.5"
                    >
                      {{ day.weather.temperature }}°
                    </div>
                  </div>
                }
              </div>

              <!-- Barre de progression tâches -->
              @if (day.tasks > 0) {
                <div class="mb-2">
                  <div
                    class="flex justify-between text-xs text-gray-400 dark:text-slate-500 mb-0.5"
                  >
                    <span>Préparation</span>
                    <span>{{ day.completedTasks }}/{{ day.tasks }}</span>
                  </div>
                  <div
                    class="h-1.5 rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden"
                  >
                    <div
                      class="h-full rounded-full transition-all"
                      [class]="
                        day.completedTasks === day.tasks
                          ? 'bg-green-500'
                          : 'bg-purple-500'
                      "
                      [style.width.%]="
                        day.tasks > 0
                          ? (day.completedTasks / day.tasks) * 100
                          : 0
                      "
                    ></div>
                  </div>
                </div>
              }

              <!-- Indicateurs -->
              <div class="flex flex-wrap gap-1 mt-1">
                @if (day.servicesCount > 0) {
                  <span
                    class="inline-flex items-center gap-0.5 rounded-md bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 text-xs font-medium"
                  >
                    📅 {{ day.servicesCount }}
                  </span>
                }
                @if (day.alertsCount > 0) {
                  <span
                    class="inline-flex items-center gap-0.5 rounded-md bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 px-1.5 py-0.5 text-xs font-medium"
                  >
                    ⚠️ {{ day.alertsCount }}
                  </span>
                }
                @if (day.isWeekend) {
                  <span
                    class="inline-flex items-center rounded-md bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400 px-1.5 py-0.5 text-xs"
                  >
                    Weekend
                  </span>
                }
              </div>

              <!-- Météo détaillée aujourd'hui -->
              @if (day.isToday && day.weather) {
                <div
                  class="mt-2 pt-2 border-t border-blue-100 dark:border-blue-800/40 flex items-center gap-2"
                >
                  <span class="text-2xl">{{ day.weather.icon }}</span>
                  <div class="flex flex-col">
                    @if (day.weather.condition) {
                      <span
                        class="text-xs font-medium text-gray-700 dark:text-slate-200 leading-tight"
                        >{{ day.weather.condition }}</span
                      >
                    }
                    <span class="text-xs text-gray-500 dark:text-slate-400">
                      @if (day.weather.temperatureMin !== undefined) {
                        🌅 {{ day.weather.temperatureMin }}° · ☀️
                        {{ day.weather.temperatureMax }}°
                      } @else {
                        {{ day.weather.temperature }}°C
                      }
                    </span>
                  </div>
                </div>
              }

              <!-- Périodes spéciales -->
              @if (day.specialPeriods?.length) {
                <div class="mt-2 flex flex-col gap-0.5">
                  @for (
                    sp of isDayPeriodExpanded(day)
                      ? day.specialPeriods!
                      : day.specialPeriods!.slice(0, 1);
                    track sp.label
                  ) {
                    <div
                      class="text-xs font-semibold truncate"
                      [style.color]="sp.color || '#7c3aed'"
                    >
                      {{ PERIOD_TYPE_META[sp.type]?.icon }} {{ sp.label }}
                    </div>
                  }
                  @if (day.specialPeriods!.length > 1) {
                    <button
                      (click)="togglePeriodExpand(day, $event)"
                      class="text-[10px] text-purple-500 dark:text-purple-400 hover:underline text-left"
                    >
                      {{
                        isDayPeriodExpanded(day)
                          ? "− Réduire"
                          : "+" + (day.specialPeriods!.length - 1) + " autre(s)"
                      }}
                    </button>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    } @else {
      <!-- ─── Vue multi-semaine (1m / 3m / 1y) ─── -->
      <div class="p-4 sm:p-6">
        <div class="">
          <!-- En-tête jours -->
          <div class="grid grid-cols-7 gap-1.5 mb-2 min-w-[420px]">
            @for (
              d of ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
              track $index
            ) {
              <div
                class="text-center text-xs font-semibold text-gray-400 dark:text-slate-500 py-1"
              >
                {{ d }}
              </div>
            }
          </div>
          <!-- Grille jours -->
          <div class="grid grid-cols-7 gap-1.5 min-w-[420px]">
            @for (blank of getLeadingBlanks(); track blank) {
              <div></div>
            }
            @for (day of calendarDays; track day.date) {
              <div
                (click)="selectDay(day)"
                class="relative cursor-pointer rounded-xl border transition-all duration-200 hover:shadow-md overflow-hidden"
                [class]="
                  selectedDay && isSameDay(day.date, selectedDay.date)
                    ? 'border-purple-400 ring-2 ring-purple-500 shadow-lg'
                    : day.isToday
                      ? 'border-blue-300 dark:border-blue-600'
                      : day.specialPeriods?.length
                        ? 'border-gray-200 dark:border-slate-700 hover:opacity-90'
                        : day.isWeekend
                          ? 'border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/40 hover:bg-gray-100 dark:hover:bg-slate-700'
                          : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-750'
                "
                [style.background-color]="
                  day.specialPeriods?.length
                    ? (day.specialPeriods![0].color || '#a78bfa') + '22'
                    : null
                "
              >
                <!-- Vue 1m : carte riche -->
                <div class="p-1.5 sm:p-2 flex flex-col gap-0.5">
                  <!-- Ligne 1 : jour abrégé + météo -->
                  <div class="flex items-center justify-between">
                    <span
                      class="text-[10px] font-semibold uppercase text-gray-400 dark:text-slate-500 truncate"
                      >{{ day.date | date: "EEE" }}</span
                    >
                    @if (day.weather?.icon) {
                      <span
                        class="text-[10px] text-gray-500 dark:text-slate-400 whitespace-nowrap"
                        >{{ day.weather.icon }}
                        @if (day.weather.temperatureMin !== undefined) {
                          ↓{{ day.weather.temperatureMin }}°↑{{
                            day.weather.temperatureMax
                          }}°
                        } @else {
                          {{ day.weather.temperature }}°
                        }
                      </span>
                    }
                  </div>
                  <!-- Ligne 2 : numéro du jour -->
                  @if (day.isToday) {
                    <span
                      class="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center leading-none"
                    >
                      {{ day.date | date: "d" }}
                    </span>
                  } @else {
                    <span
                      class="text-lg font-bold text-gray-900 dark:text-white leading-none"
                      >{{ day.date | date: "d" }}</span
                    >
                  }
                  <!-- Ligne 3 : services -->
                  @if (day.servicesCount > 0) {
                    <div
                      class="flex items-center gap-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1 py-0.5 text-[10px] font-medium"
                    >
                      <span>📅</span>
                      <span class="truncate"
                        >{{ day.servicesCount }} service{{
                          day.servicesCount > 1 ? "s" : ""
                        }}</span
                      >
                    </div>
                  }
                  <!-- Ligne 4 : tâches -->
                  @if (day.tasks > 0) {
                    <div
                      class="flex items-center gap-0.5 text-[10px] text-gray-500 dark:text-slate-400"
                    >
                      <span>{{
                        day.completedTasks === day.tasks ? "✅" : "⏳"
                      }}</span>
                      <span class="truncate"
                        >Préparation {{ day.completedTasks }}/{{
                          day.tasks
                        }}</span
                      >
                    </div>
                  }
                  <!-- Ligne 5 : alertes -->
                  @if (day.alertsCount > 0) {
                    <div class="text-[10px] font-medium text-red-500">
                      ⚠️ {{ day.alertsCount }} alerte{{
                        day.alertsCount > 1 ? "s" : ""
                      }}
                    </div>
                  }
                  <!-- Ligne 6 : périodes spéciales -->
                  @if (day.specialPeriods?.length) {
                    <div class="flex flex-col gap-0.5">
                      @for (
                        sp of isDayPeriodExpanded(day)
                          ? day.specialPeriods!
                          : day.specialPeriods!.slice(0, 1);
                        track sp.label
                      ) {
                        <div
                          class="text-[10px] font-medium truncate"
                          [style.color]="sp.color || '#7c3aed'"
                        >
                          {{ PERIOD_TYPE_META[sp.type]?.icon }}{{ sp.label }}
                        </div>
                      }
                      @if (day.specialPeriods!.length > 1) {
                        <button
                          (click)="togglePeriodExpand(day, $event)"
                          class="text-[9px] text-purple-400 hover:underline text-left leading-tight"
                        >
                          {{
                            isDayPeriodExpanded(day)
                              ? "−"
                              : "+" + (day.specialPeriods!.length - 1)
                          }}
                        </button>
                      }
                    </div>
                  }
                  <!-- Météo détaillée aujourd'hui (1m) -->
                  @if (day.isToday && day.weather) {
                    <div
                      class="mt-1 pt-1 border-t border-blue-100 dark:border-blue-800/40 flex items-center gap-1"
                    >
                      <span class="text-base">{{ day.weather.icon }}</span>
                      <div class="flex flex-col leading-none">
                        @if (day.weather.condition) {
                          <span
                            class="text-[9px] font-medium text-gray-700 dark:text-slate-200 truncate"
                            >{{ day.weather.condition }}</span
                          >
                        }
                        <span
                          class="text-[9px] text-gray-500 dark:text-slate-400"
                        >
                          @if (day.weather.temperatureMin !== undefined) {
                            🌅 {{ day.weather.temperatureMin }}° · ☀️
                            {{ day.weather.temperatureMax }}°
                          } @else {
                            {{ day.weather.temperature }}°
                          }
                        </span>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }
  </section>

  <!-- Modal : formulaire période spéciale -->
  @if (showPeriodForm()) {
    <app-special-period-form
      [period]="editingPeriod()"
      (saved)="onPeriodSaved()"
      (cancelled)="closePeriodForm()"
    ></app-special-period-form>
  }

  <!-- Modal : détail jour calendrier -->
  @if (showCalendarDayModal && selectedDay) {
    <div
      class="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      (click)="closeCalendarDayModal()"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto"
        (click)="$event.stopPropagation()"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800 rounded-t-2xl z-10"
        >
          <div>
            <h2
              class="text-lg font-bold text-gray-900 dark:text-white capitalize"
            >
              {{ selectedDay.date | date: "EEEE d MMMM" }}
            </h2>
            @if (selectedDay.weather) {
              <div class="flex items-center gap-3 mt-1">
                <span class="text-3xl">{{ selectedDay.weather.icon }}</span>
                <div>
                  @if (selectedDay.weather.condition) {
                    <p
                      class="text-sm font-medium text-gray-700 dark:text-slate-200"
                    >
                      {{ selectedDay.weather.condition }}
                    </p>
                  }
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    @if (selectedDay.weather.temperatureMin !== undefined) {
                      🌅 Matin {{ selectedDay.weather.temperatureMin }}° · ☀️
                      Après-midi {{ selectedDay.weather.temperatureMax }}°
                    } @else {
                      {{ selectedDay.weather.temperature }}°C
                    }
                  </p>
                </div>
              </div>
            } @else {
              <span
                class="inline-block mt-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500"
              >
                💫 Météo indisponible (&gt;7j)
              </span>
            }
          </div>
          <button
            (click)="closeCalendarDayModal()"
            class="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-300 text-2xl leading-none w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition"
          >
            ✕
          </button>
        </div>

        <!-- Body -->
        <div class="p-5 space-y-4">
          <!-- Périodes spéciales -->
          @if (selectedDay.specialPeriods?.length) {
            @for (sp of selectedDay.specialPeriods!; track sp.label) {
              <div
                class="flex items-center gap-3 p-3 rounded-xl border"
                [style.border-color]="sp.color || '#a78bfa'"
                [style.background-color]="(sp.color || '#a78bfa') + '18'"
              >
                <span class="text-xl">{{
                  PERIOD_TYPE_META[sp.type]?.icon
                }}</span>
                <div class="flex-1 min-w-0">
                  <p
                    class="font-medium text-sm"
                    [style.color]="sp.color || '#7c3aed'"
                  >
                    {{ sp.label }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Période spéciale active
                  </p>
                </div>
                <button
                  (click)="openPeriodForm(merchantPeriods().find(p => p.label === sp.label) ?? null); closeCalendarDayModal()"
                  class="ml-auto text-xs text-purple-600 dark:text-purple-400 hover:underline whitespace-nowrap"
                >
                  Modifier
                </button>
              </div>
            }
          }

          <!-- Services + préparation -->
          @if (selectedDay.servicesCount > 0) {
            <div
              class="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800"
            >
              <div class="flex items-center gap-2 mb-2">
                <span class="text-xl">📅</span>
                <p class="font-medium text-gray-900 dark:text-white">
                  {{ selectedDay.servicesCount }} service(s) planifié(s)
                </p>
              </div>
              @if (selectedDay.tasks > 0) {
                <div>
                  <div
                    class="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1"
                  >
                    <span>Préparation</span>
                    <span
                      >{{ selectedDay.completedTasks }}/{{
                        selectedDay.tasks
                      }}</span
                    >
                  </div>
                  <div
                    class="h-2 rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden"
                  >
                    <div
                      class="h-full rounded-full transition-all"
                      [class]="
                        selectedDay.completedTasks === selectedDay.tasks
                          ? 'bg-green-500'
                          : 'bg-purple-500'
                      "
                      [style.width.%]="
                        selectedDay.tasks > 0
                          ? (selectedDay.completedTasks / selectedDay.tasks) *
                            100
                          : 0
                      "
                    ></div>
                  </div>
                </div>
              }
            </div>
          }

          <!-- Alertes -->
          @if (selectedDay.alertsCount > 0) {
            <div
              class="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800"
            >
              <div class="flex items-center gap-2">
                <span class="text-xl">⚠️</span>
                <p class="font-medium text-gray-900 dark:text-white">
                  {{ selectedDay.alertsCount }} alerte(s)
                </p>
              </div>
            </div>
          }

          <!-- Affluence -->
          @if (selectedDay.affluence) {
            <div class="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-600 dark:text-gray-300"
                  >👥 Affluence prévue</span
                >
                <span
                  class="text-sm font-semibold capitalize text-gray-900 dark:text-white"
                  >{{ selectedDay.affluence }}</span
                >
              </div>
            </div>
          }

          <!-- Rien -->
          @if (
            !selectedDay.servicesCount &&
            !selectedDay.alertsCount &&
            !selectedDay.specialPeriod
          ) {
            <div class="text-center py-8 text-gray-500 dark:text-gray-400">
              <p class="text-4xl mb-3">💭</p>
              <p class="text-sm">Aucune activité prévue ce jour.</p>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Recommandations du jour - PREMIUM VALUE SECTION -->
  <div
    class="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-2xl shadow-2xl overflow-hidden border-2 border-white/20"
  >
    <!-- Header Premium avec VALEUR EN GROS -->
    <div
      (click)="toggleRecommendations()"
      class="p-6 text-white cursor-pointer hover:bg-black/10 transition-all"
    >
      <div
        class="flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
      >
        <!-- Gauche: Infos + ÉCONOMIES -->
        <div class="flex-1">
          <div class="flex items-center gap-3 mb-3">
            <div
              class="w-12 h-12 bg-white dark:bg-slate-800/20 rounded-xl flex items-center justify-center backdrop-blur-sm"
            >
              <span class="text-3xl">💎</span>
            </div>
            <div>
              <h2 class="text-2xl font-bold">{{ selectedDayLabel }}</h2>
              <p class="text-sm text-white/80">
                {{ selectedDayInfo.dayName }} {{ selectedDayInfo.dayNumber }}
                {{ selectedDayInfo.month }} • {{ selectedDayInfo.weather.icon }}
                {{ selectedDayInfo.weather.temperature }}°C • 👥
                {{ selectedDayInfo.affluence }}
              </p>
            </div>
          </div>

          <!-- ÉCONOMIES POTENTIELLES - GROS ET VISIBLE -->
          <div
            class="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-xl p-4 inline-flex items-center gap-4 shadow-lg dark:shadow-slate-900/50"
          >
            <div
              class="w-14 h-14 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center"
            >
              <span class="text-3xl">💰</span>
            </div>
            <div>
              <div
                class="text-xs font-semibold text-orange-900 dark:text-orange-200 uppercase tracking-wide"
              >
                Économies estimées
              </div>
              <div
                class="text-3xl font-black text-white drop-shadow-md dark:shadow-slate-900/50"
              >
                {{ getWasteOnlyLoss() + getMissedSalesLoss() }}€
              </div>
              <div class="text-xs text-orange-900 dark:text-orange-200">
                vs gestion manuelle
              </div>
            </div>
          </div>
        </div>

        <!-- Droite: Stats + CTA -->
        <div class="flex flex-col items-end gap-3">
          <div
            class="flex items-center gap-3 bg-white/15 rounded-lg px-4 py-2 backdrop-blur-sm"
          >
            <div class="text-right">
              <div class="text-xs text-white/70">Précision moyenne</div>
              <div class="text-2xl font-bold text-white">89%</div>
            </div>
            <div class="w-16 h-16">
              <svg class="transform -rotate-90" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="rgba(255,255,255,0.2)"
                  stroke-width="3"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#10B981"
                  stroke-width="3"
                  stroke-dasharray="89, 100"
                />
              </svg>
            </div>
          </div>

          <button
            (click)="toggleRecommendations(); $event.stopPropagation()"
            class="px-8 py-4 bg-white dark:bg-slate-800 text-purple-600 rounded-xl font-bold hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition-all shadow-xl dark:shadow-slate-900/60 hover:shadow-2xl hover:scale-105 flex items-center gap-2"
          >
            <span>{{
              showRecommendations ? "▼ Masquer" : "▶ Voir le détail"
            }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Contenu de l'accordéon -->
    @if (showRecommendations) {
      <div
        class="bg-gradient-to-b from-gray-50 to-white dark:from-slate-800 dark:to-slate-800 border-t-4 border-yellow-400 dark:border-yellow-700"
        [@slideDown]
      >
        <div class="p-6 space-y-6">
          <!-- AVANT/APRÈS - PREUVE DE VALEUR -->
          <!-- Vue compacte (par défaut) -->
          @if (!showDetailedComparison) {
            <div>
              <div class="grid md:grid-cols-2 gap-4">
                <!-- SANS - Version compacte -->
                <div
                  class="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl p-4"
                >
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center gap-3">
                      <div
                        class="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center shrink-0"
                      >
                        <span class="text-xl">❌</span>
                      </div>
                      <div>
                        <h4 class="font-bold text-gray-900 dark:text-white">
                          Sans recommandations
                        </h4>
                        <p class="text-xs text-gray-600 dark:text-gray-400">
                          Gestion manuelle estimée
                        </p>
                      </div>
                    </div>
                    <div class="text-right">
                      <div
                        class="text-2xl font-black text-red-700 dark:text-red-300"
                      >
                        {{ getBaseRevenue() - getWasteOnlyLoss() }}€
                      </div>
                    </div>
                  </div>
                  <div class="flex justify-end">
                    <button
                      (click)="toggleDetailedComparison()"
                      class="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:text-red-300 font-medium bg-red-100 dark:bg-red-900/30 hover:bg-red-200 px-3 py-1 rounded-full transition-colors"
                    >
                      Détails
                    </button>
                  </div>
                </div>
                <!-- AVEC - Version compacte -->
                <div
                  class="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-400 dark:border-green-700 rounded-xl p-4 shadow-lg dark:shadow-slate-900/50"
                >
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex items-center gap-3">
                      <div
                        class="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center shrink-0"
                      >
                        <span class="text-xl">✅</span>
                      </div>
                      <div>
                        <h4 class="font-bold text-gray-900 dark:text-white">
                          Avec Predivent
                        </h4>
                        <p class="text-xs text-gray-600 dark:text-gray-400">
                          Optimisé par IA
                        </p>
                      </div>
                      <span
                        class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-sm dark:shadow-slate-900/30 ml-4"
                      >
                        +{{ getWasteOnlyLoss() + getMissedSalesLoss() }}€
                      </span>
                    </div>
                    <div class="text-right">
                      <div
                        class="text-2xl font-black text-green-700 dark:text-green-300"
                      >
                        {{ getBaseRevenue() + getMissedSalesLoss() }}€
                      </div>
                    </div>
                  </div>
                  <div class="flex justify-end">
                    <button
                      (click)="toggleDetailedComparison()"
                      class="text-xs text-green-600 dark:text-green-400 hover:text-green-800 dark:text-green-300 font-medium bg-green-100 dark:bg-green-900/30 hover:bg-green-200 px-3 py-1 rounded-full transition-colors"
                    >
                      Détails
                    </button>
                  </div>
                </div>
              </div>
            </div>
          }
          <!-- Vue détaillée (sur demande) -->
          @if (showDetailedComparison) {
            <div>
              <div class="grid md:grid-cols-2 gap-4">
                <!-- SANS Predivent - Détaillé -->
                <div
                  class="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl p-5 flex flex-col"
                >
                  <div class="flex items-center gap-3 mb-3">
                    <div
                      class="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center shrink-0"
                    >
                      <span class="text-xl">❌</span>
                    </div>
                    <div>
                      <h4 class="font-bold text-gray-900 dark:text-white">
                        Sans recommandations
                      </h4>
                      <p class="text-xs text-gray-600 dark:text-gray-400">
                        Gestion manuelle estimée
                      </p>
                    </div>
                  </div>
                  <div
                    class="space-y-2 text-sm flex-1 flex flex-col justify-between"
                  >
                    <div class="space-y-2">
                      <div class="flex justify-between items-center">
                        <span class="text-gray-600 dark:text-gray-400"
                          >💵 Chiffre d'affaires</span
                        >
                        <span
                          class="font-bold text-gray-700 dark:text-gray-300 text-right min-w-[80px]"
                          >{{ getBaseRevenue() }}€</span
                        >
                      </div>
                      <div class="flex justify-between items-center">
                        <span class="text-gray-600 dark:text-gray-400"
                          >🗑️ Gaspillage (perte)</span
                        >
                        <span
                          class="font-bold text-red-600 dark:text-red-400 text-right min-w-[80px]"
                          >-{{ getWasteOnlyLoss() }}€</span
                        >
                      </div>
                    </div>
                    <div class="space-y-2 pt-2">
                      <div
                        class="border-t-2 border-red-300 pt-2 flex justify-between items-center"
                      >
                        <span class="font-bold text-gray-900 dark:text-white"
                          >CA total effectif</span
                        >
                        <span
                          class="font-black text-red-700 dark:text-red-300 text-lg text-right min-w-[80px]"
                          >{{ getBaseRevenue() - getWasteOnlyLoss() }}€</span
                        >
                      </div>
                      <div
                        class="flex justify-between items-center bg-orange-50 dark:bg-orange-900/20 rounded-lg px-3 py-2 border border-orange-200 dark:border-orange-800"
                      >
                        <span
                          class="text-xs text-orange-700 dark:text-orange-300"
                          >📉 Ventes manquées</span
                        >
                        <span
                          class="text-xs font-bold text-orange-700 dark:text-orange-300 text-right min-w-[80px]"
                          >-{{ getMissedSalesLoss() }}€</span
                        >
                      </div>
                      <div
                        class="flex justify-between items-center bg-orange-50 dark:bg-orange-900/20 rounded-lg px-3 py-2 border border-orange-200 dark:border-orange-800"
                      >
                        <span class="text-xs text-gray-700 dark:text-gray-300"
                          >⏱️ Temps gestion</span
                        >
                        <span
                          class="text-xs font-bold text-orange-700 dark:text-orange-300 text-right min-w-[80px]"
                          >2h/jour</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
                <!-- AVEC Predivent - Détaillé -->
                <div
                  class="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-400 dark:border-green-700 rounded-xl p-5 shadow-lg dark:shadow-slate-900/50 flex flex-col"
                >
                  <div class="flex items-center gap-3 mb-3">
                    <div
                      class="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center shrink-0"
                    >
                      <span class="text-xl">✅</span>
                    </div>
                    <div>
                      <h4 class="font-bold text-gray-900 dark:text-white">
                        Avec Predivent
                      </h4>
                      <p class="text-xs text-gray-600 dark:text-gray-400">
                        Optimisé par IA
                      </p>
                    </div>
                  </div>
                  <div
                    class="space-y-2 text-sm flex-1 flex flex-col justify-between"
                  >
                    <div class="space-y-2">
                      <div class="flex justify-between items-center">
                        <span class="text-gray-600 dark:text-gray-400"
                          >💵 Chiffre d'affaires optimisé</span
                        >
                        <span
                          class="font-bold text-gray-700 dark:text-gray-300 text-right min-w-[80px]"
                          >{{ getBaseRevenue() }}€</span
                        >
                      </div>
                      <div
                        class="flex justify-between items-center text-gray-400"
                      >
                        <span>🗑️ Gaspillage</span>
                        <span class="font-bold text-right min-w-[80px]"
                          >0€</span
                        >
                      </div>
                      <div
                        class="flex justify-between items-center bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-3 py-2 border border-emerald-300"
                      >
                        <span
                          class="text-xs text-emerald-700 dark:text-emerald-300"
                          >✨ Ventes supplémentaires</span
                        >
                        <span
                          class="text-xs font-bold text-emerald-700 dark:text-emerald-300 text-right min-w-[80px]"
                          >+{{ getMissedSalesLoss() }}€</span
                        >
                      </div>
                    </div>
                    <div class="space-y-2 pt-2">
                      <div
                        class="border-t-2 border-green-400 dark:border-green-700 pt-2 flex justify-between items-center"
                      >
                        <div class="flex items-center gap-2">
                          <span class="font-bold text-gray-900 dark:text-white"
                            >CA total effectif</span
                          >
                          <span
                            class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-sm dark:shadow-slate-900/30"
                          >
                            +{{ getWasteOnlyLoss() + getMissedSalesLoss() }}€
                          </span>
                        </div>
                        <span
                          class="font-black text-green-700 dark:text-green-300 text-lg text-right min-w-[80px]"
                          >{{ getBaseRevenue() + getMissedSalesLoss() }}€</span
                        >
                      </div>
                      <div
                        class="flex justify-between items-center bg-green-100 dark:bg-green-900/30 rounded-lg px-3 py-2 border border-green-300"
                      >
                        <span class="text-xs text-gray-700 dark:text-gray-300"
                          >⚡ Temps gagné</span
                        >
                        <span
                          class="text-xs font-bold text-green-700 dark:text-green-300 text-right min-w-[80px]"
                          >2h/jour économisées</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Bouton pour masquer les détails -->
              <div class="text-center mt-3">
                <button
                  (click)="toggleDetailedComparison()"
                  class="text-sm text-gray-600 dark:text-gray-400 hover:text-purple-600 font-medium transition-colors flex items-center gap-1 mx-auto"
                >
                  <span>Masquer les détails</span>
                  <span>▲</span>
                </button>
              </div>
            </div>
          }
          <!-- Services - Vue compacte et moderne avec comparaison -->
          <div class="space-y-3">
            @for (
              service of filteredDailyRecommendations;
              track service;
              let i = $index
            ) {
              <div
                class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 hover:border-purple-300 transition-all overflow-hidden"
              >
                <!-- Header ultra-compact avec comparaison -->
                <div
                  class="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20"
                >
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center gap-3 flex-1">
                      <div
                        class="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-md dark:shadow-slate-900/50"
                      >
                        <span class="text-xl">{{ service.icon }}</span>
                      </div>
                      <div>
                        <h3
                          class="text-base font-bold text-gray-900 dark:text-white"
                        >
                          {{ service.service }}
                        </h3>
                        <span class="text-xs text-gray-600 dark:text-gray-400"
                          >{{ service.timeSlot }} •
                          {{ service.products.length }} items</span
                        >
                      </div>
                    </div>
                    <button
                      (click)="exportServicePDF(service)"
                      class="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-md dark:shadow-slate-900/50 hover:shadow-lg dark:shadow-slate-900/50 hover:scale-105"
                      title="Télécharger les quantités pour ce service"
                    >
                      <span class="hidden sm:inline">📥 PDF</span>
                      <span class="sm:hidden">📥</span>
                    </button>
                  </div>
                </div>
                <!-- Grid produits (top 6 ou tous selon état) -->
                <div
                  class="p-3 bg-gradient-to-br from-purple-50/50 to-pink-50/50 dark:from-purple-900/20 dark:to-pink-900/20"
                >
                  <div class="grid grid-cols-4 md:grid-cols-8 gap-1.5 mb-2">
                    @for (
                      product of isServiceExpanded(i)
                        ? service.products
                        : service.products.slice(0, 8);
                      track product;
                      let pi = $index
                    ) {
                      <div
                        class="bg-white dark:bg-slate-800 rounded-lg p-1.5 border border-purple-100 hover:shadow-lg dark:shadow-slate-900/50 hover:scale-105 transition-all cursor-pointer group relative"
                        [title]="
                          pi % 3 === 0
                            ? 'Sans outil: ' +
                              Math.round(product.recommended * 0.75) +
                              ' unités (rupture stock) → Avec Predivent: ' +
                              product.recommended +
                              ' unités (+' +
                              Math.round(product.recommended * 0.25) +
                              ' ventes supplémentaires)'
                            : 'Sans outil: ' +
                              Math.round(product.recommended * 1.3) +
                              ' unités (sur-préparation) → Avec Predivent: ' +
                              product.recommended +
                              ' unités (-' +
                              Math.round(product.recommended * 0.3) +
                              ' gaspillage évité)'
                        "
                      >
                        <!-- Badge variable : opportunité vente OU économie gaspillage -->
                        @if (pi % 3 === 0) {
                          <div
                            class="absolute -top-1 -right-1 px-1 py-0.5 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-full text-[10px] font-bold shadow-md dark:shadow-slate-900/50"
                          >
                            <span
                              >+{{
                                Math.round(product.recommended * 0.25)
                              }}</span
                            >
                          </div>
                        }
                        @if (pi % 3 !== 0) {
                          <div
                            class="absolute -top-1 -right-1 px-1 py-0.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full text-[10px] font-bold shadow-md dark:shadow-slate-900/50"
                          >
                            <span
                              >-{{
                                Math.round(product.recommended * 0.3)
                              }}</span
                            >
                          </div>
                        }
                        <div class="text-center">
                          <span
                            class="text-xl block mb-0.5 group-hover:scale-110 transition-transform"
                            >{{ product.icon }}</span
                          >
                          <div
                            class="text-lg font-black text-purple-700 dark:text-purple-300"
                          >
                            {{ product.recommended }}
                          </div>
                          <div
                            class="text-[10px] text-gray-900 dark:text-white font-medium line-clamp-1"
                          >
                            {{ product.name }}
                          </div>
                          <div
                            class="text-[9px] text-gray-500 dark:text-gray-400"
                          >
                            {{ product.range }}
                          </div>
                          <!-- Message différent selon le type d'optimisation -->
                          @if (pi % 3 === 0) {
                            <div
                              class="text-[10px] font-bold text-orange-600 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ≈ +{{
                                getProductSavings(pi, product.recommended)
                              }}€ ventes
                            </div>
                          }
                          @if (pi % 3 !== 0) {
                            <div
                              class="text-[10px] font-bold text-green-600 dark:text-green-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ≈ -{{
                                getProductSavings(pi, product.recommended)
                              }}€ gaspillage
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                  <!-- Bouton voir plus/moins -->
                  <div class="flex items-center gap-2">
                    @if (service.products.length > 8) {
                      <button
                        (click)="toggleServiceExpansion(i)"
                        class="flex-1 py-2 text-xs text-purple-700 dark:text-purple-300 hover:text-purple-900 font-semibold hover:bg-white dark:bg-slate-800 rounded-lg transition-all border border-purple-200 dark:border-purple-800 hover:border-purple-300"
                      >
                        {{
                          isServiceExpanded(i)
                            ? "▲ Voir moins"
                            : "▼ Voir les " +
                              (service.products.length - 8) +
                              " autres"
                        }}
                      </button>
                    }
                    <!-- Info contextuelle compacte -->
                    <div class="flex-1">
                      <div
                        class="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 cursor-pointer hover:bg-blue-100 dark:bg-blue-900/30 transition-colors"
                        (click)="toggleExplanationDetails()"
                      >
                        <span class="text-sm">💡</span>
                        <span class="flex-1"
                          ><span
                            class="font-semibold text-blue-700 dark:text-blue-300"
                            >Optimisé pour</span
                          >
                          {{ selectedDayInfo.weather.temperature }}°C •
                          Affluence {{ selectedDayInfo.affluence }}</span
                        >
                        <span class="text-blue-600 dark:text-blue-400">{{
                          showExplanationDetails ? "▲" : "▼"
                        }}</span>
                      </div>
                      <!-- Détails explicatifs (affiché sur clic) -->
                      @if (showExplanationDetails) {
                        <div
                          class="mt-2 p-3 bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-gray-700 dark:text-gray-300 space-y-2 shadow-sm dark:shadow-slate-900/30"
                        >
                          <div
                            class="font-semibold text-blue-700 dark:text-blue-300 mb-2"
                          >
                            📊 Comment sont calculées ces recommandations ?
                          </div>
                          <div class="space-y-1.5">
                            <div class="flex gap-2">
                              <span class="text-blue-600 dark:text-blue-400"
                                >🌡️</span
                              >
                              <span
                                ><strong
                                  >Météo ({{
                                    selectedDayInfo.weather.temperature
                                  }}°C) :</strong
                                >
                                Influence la demande en boissons chaudes/froides
                                et produits saisonniers.</span
                              >
                            </div>
                            <div class="flex gap-2">
                              <span class="text-blue-600 dark:text-blue-400"
                                >👥</span
                              >
                              <span
                                ><strong
                                  >Affluence ({{ selectedDayInfo.affluence }})
                                  :</strong
                                >
                                Basée sur l'historique du jour de la semaine et
                                des événements locaux.</span
                              >
                            </div>
                            <div class="flex gap-2">
                              <span class="text-blue-600 dark:text-blue-400"
                                >📈</span
                              >
                              <span
                                ><strong>Historique de ventes :</strong> Analyse
                                des 12 dernières semaines pour identifier les
                                tendances et la saisonnalité.</span
                              >
                            </div>
                            <div class="flex gap-2">
                              <span class="text-blue-600 dark:text-blue-400"
                                >🎯</span
                              >
                              <span
                                ><strong>Marges de confiance :</strong>
                                Fourchettes (min-max) pour gérer l'incertitude
                                et éviter les ruptures.</span
                              >
                            </div>
                          </div>
                          <div
                            class="pt-2 border-t border-blue-100 text-blue-800 dark:text-blue-300"
                          >
                            <strong>💡 Résultat :</strong> Quantités optimales
                            pour maximiser vos ventes tout en minimisant le
                            gaspillage.
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
          <!-- Actions principales compactes -->
          <div class="flex justify-center gap-2 pt-3">
            <button
              (click)="exportCompletePDF()"
              class="px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg dark:shadow-slate-900/50 hover:shadow-xl dark:shadow-slate-900/60 flex items-center gap-2"
              title="Télécharger la fiche complète avec tous les services"
            >
              <span class="text-xl">📥</span>
              <span class="text-sm">PDF complet</span>
            </button>
            <button
              class="px-4 py-3 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold hover:border-purple-300 hover:bg-purple-50 dark:bg-purple-900/20 transition-all flex items-center gap-2"
            >
              <span class="text-lg">⚙️</span>
              <span class="text-sm">Ajuster</span>
            </button>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Widget Saisie manuelle & Checklist préparation - 2 colonnes -->
  <div class="grid lg:grid-cols-2 gap-6 mb-8">
    <!-- Widget Saisie manuelle des ventes -->
    <section
      class="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg dark:shadow-slate-900/50 p-6 text-white"
    >
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold mb-2">📝 Saisie manuelle des ventes</h2>
          <p class="text-blue-100">
            Enregistrez vos ventes pour améliorer les recommandations
          </p>
        </div>
        <div class="flex gap-4">
          <a
            [routerLink]="['/services']"
            class="text-center bg-white/15 rounded-lg p-4 backdrop-blur-sm hover:bg-white/25 transition-all cursor-pointer"
          >
            <div class="text-3xl font-bold text-white">
              {{ monthlySalesCount }}
            </div>
            <div class="text-xs text-blue-100">Saisies ce mois</div>
          </a>
          <div class="text-center bg-white/15 rounded-lg p-4 backdrop-blur-sm">
            <div class="text-3xl font-bold text-white">
              {{ monthlyAverageAccuracy }}%
            </div>
            <div class="text-xs text-blue-100">Précision moyenne</div>
          </div>
        </div>
      </div>
      <div class="mt-6">
        <a
          [routerLink]="['/sales/entry-fixed']"
          class="inline-block px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition shadow-md"
        >
          ➕ Nouvelle saisie
        </a>
      </div>
    </section>

    <!-- Checklist préparation PREMIUM -->
    <div
      class="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20 relative"
    >
      <div class="p-6 text-white">
        <div class="flex items-center gap-4 mb-4">
          <div
            class="w-16 h-16 bg-white dark:bg-slate-800/20 rounded-xl flex items-center justify-center backdrop-blur-sm shadow-lg dark:shadow-slate-900/50"
          >
            <span class="text-4xl">✅</span>
          </div>
          <div class="flex-1">
            <h2 class="text-2xl font-bold">Checklist de préparation</h2>
            @if (planFeatureService.canAccess(FEATURES.PREPARATION_CHECKLIST)) {
              <p class="text-sm text-blue-100 mt-1">
                {{ getCompletedTasksCount() }}/{{ preparationTasks.length }}
                tâches complétées • Économie potentielle:
                <span class="font-bold text-yellow-300 text-base">12€</span>
              </p>
            }
          </div>
          @if (planFeatureService.canAccess(FEATURES.PREPARATION_CHECKLIST)) {
            <!-- Barre de progression circulaire -->
            <div class="relative w-20 h-20">
              <svg class="transform -rotate-90" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="rgba(255,255,255,0.2)"
                  stroke-width="3"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#FCD34D"
                  stroke-width="3"
                  [attr.stroke-dasharray]="
                    (getCompletedTasksCount() / preparationTasks.length) * 100 +
                    ', 100'
                  "
                />
              </svg>
              <div class="absolute inset-0 flex items-center justify-center">
                <span class="text-base font-bold"
                  >{{
                    Math.floor(
                      (getCompletedTasksCount() / preparationTasks.length) * 100
                    )
                  }}%</span
                >
              </div>
            </div>
          }
        </div>
        <div class="mt-6">
          @if (planFeatureService.canAccess(FEATURES.PREPARATION_CHECKLIST)) {
            <button
              (click)="openChecklist()"
              class="inline-block px-6 py-3 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-blue-50 transition shadow-md"
            >
              📋 Voir le détail
            </button>
          } @else {
            <a
              routerLink="/subscription"
              class="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800/20 text-white font-semibold rounded-lg hover:bg-white dark:bg-slate-800/30 transition shadow-md dark:shadow-slate-900/50"
            >
              🔒 Débloquer avec Pro
            </a>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Alertes et Recommandations prioritaires -->
  <div class="grid lg:grid-cols-2 gap-6 mb-8">
    <!-- Alertes importantes -->
    <div
      class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-6"
    >
      <div class="flex items-center justify-between mb-6">
        <h2
          class="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2"
        >
          <span>⚠️</span>
          Alertes importantes
        </h2>
        @if (!planFeatureService.canAccess(FEATURES.AUTO_ALERTS)) {
          <a
            routerLink="/subscription"
            class="flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-full text-xs font-semibold text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:bg-purple-900/30 transition"
          >
            <span>🔒</span> Pro
          </a>
        } @else {
          @if (unifiedAlerts.length + merchantAlerts.length > 0) {
            <span
              class="px-3 py-1 rounded-full text-xs font-medium"
              [ngClass]="merchantAlerts.some(a => a.severity === 'critical') ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'"
              >{{ unifiedAlerts.length + merchantAlerts.length }}</span
            >
          } @else {
            <span
              class="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium"
              >0</span
            >
          }
        }
      </div>
      @if (planFeatureService.canAccess(FEATURES.AUTO_ALERTS)) {
        @if (unifiedAlerts.length > 0 || merchantAlerts.length > 0) {
          <div class="space-y-3">
            <!-- Alertes unifiées (météo / événements / calendrier) -->
            @for (alert of unifiedAlerts; track alert.date + alert.title) {
              <div
                class="border-l-4 p-4 rounded-r-lg"
                [ngClass]="alertService.getAlertClasses(alert)"
              >
                <div class="flex items-start gap-3">
                  <span class="text-2xl">{{ alert.icon }}</span>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <p
                        class="font-semibold"
                        [ngClass]="alertService.getAlertTitleClass(alert)"
                      >
                        {{ alert.title }}
                      </p>
                      <span
                        class="text-xs px-2 py-0.5 rounded-full font-bold"
                        [ngClass]="
                          alertService.getImpactBadgeClass(alert.impactPct)
                        "
                      >
                        {{ alert.impactPct > 0 ? "+" : ""
                        }}{{ alert.impactPct }}%
                      </span>
                      <span
                        class="text-xs px-2 py-0.5 rounded-full font-medium"
                        [ngClass]="
                          alertService.getConfidenceClass(alert.confidence)
                        "
                      >
                        🎯 {{ alert.confidence }}%
                      </span>
                    </div>
                    <button
                      (click)="alert.expanded = !alert.expanded"
                      class="text-xs mt-1 underline cursor-pointer"
                      [ngClass]="alertService.getAlertLinkClass(alert)"
                    >
                      {{ alert.expanded ? "Masquer" : "Voir" }} le détail
                    </button>
                    @if (alert.expanded) {
                      <p
                        class="text-sm mt-2 leading-relaxed"
                        [ngClass]="alertService.getAlertDetailClass(alert)"
                      >
                        {{ alert.detail }}
                      </p>
                    }
                  </div>
                  <div class="text-right flex-shrink-0">
                    <span
                      class="text-xs px-2 py-1 rounded block whitespace-nowrap"
                      [ngClass]="alertService.getAlertDateBadgeClass(alert)"
                    >
                      📍 {{ alert.dateLabel }}
                    </span>
                  </div>
                </div>
              </div>
            }
            <!-- Alertes cluster (waste / rupture / margin) -->
            @for (alert of merchantAlerts; track alert.type + alert.severity) {
              <div
                class="border-l-4 p-4 rounded-lg"
                [ngClass]="alertBorderColor(alert.severity)"
              >
                <p class="font-semibold text-gray-900 dark:text-white mb-1">
                  {{ alertIcon(alert.type) }}
                  {{ alert.severity === "critical" ? "🔴" : "🟠" }}
                  {{ alert.message }}
                </p>
                <p class="text-sm text-gray-700 dark:text-gray-300">
                  {{ alert.recommendation }}
                </p>
              </div>
            }
          </div>
        } @else {
          <div class="text-center py-6 text-gray-400">
            <p class="text-3xl mb-2">✅</p>
            <p class="text-sm">Aucune alerte — tout va bien !</p>
          </div>
        }
      } @else {
        <app-alert-preview
          [alerts]="unifiedAlerts"
          [extraCount]="merchantAlerts.length"
        />
      }
    </div>
    <!-- Recommandations intelligentes -->
    <div
      class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-6"
    >
      <div class="flex items-center justify-between mb-6">
        <h2
          class="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2"
        >
          <span>💡</span>
          Recommandations intelligentes
        </h2>
        @if (!planFeatureService.canAccess(FEATURES.AUTO_ALERTS)) {
          <a
            routerLink="/subscription"
            class="flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-full text-xs font-semibold text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:bg-purple-900/30 transition"
          >
            <span>🔒</span> Pro
          </a>
        }
      </div>
      @if (planFeatureService.canAccess(FEATURES.AUTO_ALERTS)) {
        @if (positiveInsights.length > 0) {
          <div class="space-y-3">
            @for (insight of positiveInsights; track insight.title) {
              <div
                class="border-l-4 p-4 rounded-lg"
                [ngClass]="{
                  'border-green-500 bg-green-50 dark:bg-green-900/20':
                    insight.color === 'green',
                  'border-purple-500 bg-purple-50 dark:bg-purple-900/20':
                    insight.color === 'purple',
                  'border-blue-500 bg-blue-50 dark:bg-blue-900/20':
                    insight.color === 'blue',
                }"
              >
                <p class="font-semibold text-gray-900 dark:text-white mb-1">
                  {{ insight.icon }} {{ insight.title }}
                </p>
                <p class="text-sm text-gray-700 dark:text-gray-300">
                  {{ insight.message }}
                </p>
              </div>
            }
          </div>
        } @else if (loading) {
          <div class="text-center py-6 text-gray-400">
            <div class="spinner w-6 h-6 border-purple-400 mx-auto mb-2"></div>
            <p class="text-sm">Chargement…</p>
          </div>
        } @else {
          <div class="text-center py-6 text-gray-400">
            <p class="text-3xl mb-2">📊</p>
            <p class="text-sm">
              Enregistrez vos ventes pour obtenir des recommandations
            </p>
          </div>
        }
      }
    </div>
  </div>
</div>

<!-- Slide-over Panel pour Checklist de préparation -->
@if (showChecklist) {
  <div class="fixed inset-0 z-[10000]" [class.closing]="isChecklistClosing">
    <!-- Overlay sombre -->
    <div
      class="absolute inset-0 bg-gray-900 bg-opacity-75 transition-opacity duration-300"
      [class.opacity-0]="isChecklistClosing"
      (click)="closeChecklist()"
    ></div>
    <!-- Panel qui glisse -->
    <div class="absolute inset-y-0 right-0 flex max-w-full pl-10">
      <div
        class="w-screen max-w-2xl transform transition-transform duration-300 ease-out"
        [class.translate-x-0]="isChecklistAnimating && !isChecklistClosing"
        [class.translate-x-full]="!isChecklistAnimating || isChecklistClosing"
      >
        <div class="flex h-full flex-col bg-white dark:bg-slate-800 shadow-2xl">
          <!-- Header du panel -->
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-6">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-4">
                <div
                  class="w-14 h-14 bg-white dark:bg-slate-800/20 rounded-xl flex items-center justify-center backdrop-blur-sm shadow-lg dark:shadow-slate-900/50"
                >
                  <span class="text-3xl">✅</span>
                </div>
                <div>
                  <h2 class="text-2xl font-bold text-white">
                    Checklist de préparation
                  </h2>
                  <p class="text-sm text-blue-100 mt-1">
                    {{ getCompletedTasksCount() }}/{{ preparationTasks.length }}
                    tâches complétées •
                    <span class="font-bold text-yellow-300"
                      >12€ d'économies potentielles</span
                    >
                  </p>
                </div>
              </div>
              <button
                (click)="closeChecklist()"
                class="rounded-lg p-2 text-white hover:bg-white dark:bg-slate-800/20 transition-all"
              >
                <svg
                  class="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <!-- Barre de progression -->
            <div
              class="mt-4 bg-white dark:bg-slate-800/20 rounded-full h-3 overflow-hidden"
            >
              <div
                class="bg-yellow-400 h-full transition-all duration-500"
                [style.width.%]="
                  (getCompletedTasksCount() / preparationTasks.length) * 100
                "
              ></div>
            </div>
          </div>
          <!-- Contenu scrollable -->
          <div class="flex-1 overflow-y-auto px-6 py-6">
            <div class="space-y-4">
              @for (task of preparationTasks; track task) {
                <div
                  class="bg-white dark:bg-slate-800 rounded-xl p-5 border-2 hover:border-indigo-300 hover:shadow-lg dark:shadow-slate-900/50 transition-all cursor-pointer"
                  (click)="toggleTask(task.id)"
                  [ngClass]="{
                    'opacity-70 bg-green-50 dark:bg-green-900/20 border-green-300':
                      task.completed,
                    'border-red-300 bg-red-50 dark:bg-red-900/20 ring-2 ring-red-200':
                      task.priority === 'high' && !task.completed,
                    'border-gray-200 dark:border-slate-700':
                      task.priority !== 'high' && !task.completed,
                  }"
                >
                  <div class="flex items-start gap-4">
                    <!-- Checkbox premium -->
                    <div class="mt-1">
                      <div
                        class="w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-all shadow-md dark:shadow-slate-900/50"
                        [ngClass]="{
                          'bg-gradient-to-br from-green-400 to-emerald-500 border-green-500':
                            task.completed,
                          'border-gray-400 dark:border-slate-500 bg-white dark:bg-slate-800':
                            !task.completed,
                        }"
                      >
                        @if (task.completed) {
                          <span class="text-white text-xl font-bold">✓</span>
                        }
                      </div>
                    </div>
                    <!-- Contenu -->
                    <div class="flex-1">
                      <div class="flex items-start justify-between mb-2">
                        <h4
                          class="font-bold text-gray-900 dark:text-white text-lg"
                          [ngClass]="{
                            'line-through text-gray-500 dark:text-gray-400':
                              task.completed,
                          }"
                        >
                          {{ task.title }}
                        </h4>
                        <span
                          class="text-xs font-semibold px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full whitespace-nowrap ml-2"
                        >
                          ⏰ {{ task.deadline }}
                        </span>
                      </div>
                      <div class="flex items-center gap-2 flex-wrap mb-2">
                        <span
                          class="text-xs px-3 py-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium"
                        >
                          {{ task.category }}
                        </span>
                        @if (task.priority === "high" && !task.completed) {
                          <span
                            class="text-xs px-3 py-1.5 rounded-full bg-red-500 text-white font-bold animate-pulse shadow-lg dark:shadow-slate-900/50"
                          >
                            🔥 URGENT
                          </span>
                        }
                        <!-- Impact financier -->
                        <span
                          class="text-sm px-3 py-1.5 rounded-full bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-900 font-bold border border-yellow-300"
                        >
                          💰 Évite -3€
                        </span>
                      </div>
                      @if (task.note) {
                        <p
                          class="text-sm text-gray-700 dark:text-gray-300 mt-3 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 rounded-lg border border-blue-200 dark:border-blue-800"
                        >
                          💡 {{ task.note }}
                        </p>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
          <!-- Footer avec actions -->
          <div
            class="border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 px-6 py-4 flex justify-between items-center"
          >
            <div class="text-sm text-gray-600 dark:text-gray-400">
              <span class="font-semibold">Astuce :</span> Cliquez sur une tâche
              pour la marquer comme complétée
            </div>
            <button
              (click)="closeChecklist()"
              class="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition shadow-md dark:shadow-slate-900/50"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
}
